import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { getProducts, getSettings } from '../services/publicService';
import { getPublicCategories } from '../services/publicService';
import PageLayout from '../components/PageLayout';
import SEO from '../components/SEO';
import PromoCarousel from '../components/PromoCarousel';

const ShopContainer = styled.div`
  padding: 0;
  max-width: 100%;
  margin: 0 auto;
  padding-left: 24px;
  padding-right: 24px;
  
  @media (min-width: 768px) {
    padding-left: 24px;
    padding-right: 24px;
  }
  
  @media (min-width: 1536px) {
    max-width: 1600px;
    padding-left: 0;
    padding-right: 0;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const CategoryTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  
  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e1e1e1;
  border-radius: 3px;
  background-color: #fff;
  font-size: 0.8rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #7a7a7a;
  }
`;

const CategoryTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const CategoryTag = styled(Link)`
  padding: 6px 12px;
  background-color: ${props => props.active ? '#7a7a7a' : '#e1e1e1'};
  color: ${props => props.active ? '#fff' : '#000'};
  border-radius: 3px;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-decoration: none;
  transition: all 0.2s ease;
  font-weight: 500;
  
  &:hover {
    background-color: ${props => props.active ? '#7a7a7a' : '#d1d1d1'};
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
`;

const ProductCard = styled(Link)`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  margin-bottom: 1rem;
  text-decoration: none;
  color: inherit;
  
  &:hover img {
    transform: scale(1.02);
  }
`;

const ProductImageContainer = styled.div`
  width: 100%;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProductImage = styled.img`
  width: 100%;
  aspect-ratio: 1/1.2;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductName = styled.h3`
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  @media (max-width: 480px) {
    font-size: 0.65rem;
    letter-spacing: 0;
  }
`;

const ProductPrice = styled.span`
  font-size: 0.8rem;
  font-weight: 400;
  color: #000;

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1rem;
  color: #7a7a7a;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1rem;
  color: #e53e3e;
`;

const NoProductsMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  font-size: 1.1rem;
  color: #4a4a4a;
  max-width: 600px;
  margin: 0 auto;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #333;
    font-weight: 500;
  }
  
  p {
    line-height: 1.6;
    margin-bottom: 1rem;
  }
  
  svg {
    width: 60px;
    height: 60px;
    margin-bottom: 1.5rem;
    color: #7a7a7a;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
    
    h3 {
      font-size: 1.3rem;
    }
  }
`;

const ShopPage = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [parentCategory, setParentCategory] = useState(null);
  const [promoImages, setPromoImages] = useState([]);

  // Filter states
  const [sortBy, setSortBy] = useState('newest');
  const [sizeFilter, setSizeFilter] = useState('all');

  // Get query params
  const queryParams = new URLSearchParams(location.search);
  const sort = queryParams.get('sort') || sortBy;
  const size = queryParams.get('size') || sizeFilter;

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getPublicCategories();
        if (response.success) {
          setCategories(response.data);
        }
      } catch (err) {
        // console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      }
    };

    const fetchPromoImages = async () => {
      try {
        const response = await getSettings();
        // console.log('Settings response:', response);
        if (response.success && response.settings?.homeBanner?.seasonalPromoImages) {
          // console.log('Promo images found:', response.settings.homeBanner.seasonalPromoImages);
          setPromoImages(response.settings.homeBanner.seasonalPromoImages);
        } else {
          // console.log('No promo images in response');
        }
      } catch (err) {
        // console.error('Error fetching promo images:', err);
      }
    };

    fetchCategories();
    fetchPromoImages();
  }, []);

  // Set current category and child categories based on URL
  useEffect(() => {
    if (categories.length > 0 && categorySlug) {
      // Check if it's a parent category
      const parent = categories.find(cat => cat.slug === categorySlug);
      if (parent) {
        setCurrentCategory(parent);
        setParentCategory(parent);
        setChildCategories(parent.children || []);
        return;
      }

      // Check if it's a child category
      for (const parent of categories) {
        const child = parent.children?.find(child => child.slug === categorySlug);
        if (child) {
          setCurrentCategory(child);
          setParentCategory(parent);
          setChildCategories(parent.children || []);
          return;
        }
      }
    }
  }, [categories, categorySlug]);

  // Fetch products based on filters
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        // Build query params
        const params = {};

        // Category filter
        if (categorySlug) {
          // If it's a parent category, include all child categories
          const categoryIds = [];
          const parent = categories.find(cat => cat.slug === categorySlug);
          if (parent) {
            // Add parent category ID
            categoryIds.push(parent._id);
            // Add all child category IDs
            if (parent.children && parent.children.length > 0) {
              parent.children.forEach(child => {
                categoryIds.push(child._id);
              });
            }
          } else {
            // Check if it's a child category
            for (const parentCat of categories) {
              const child = parentCat.children?.find(child => child.slug === categorySlug);
              if (child) {
                categoryIds.push(child._id);
                break;
              }
            }
          }

          if (categoryIds.length > 0) {
            params.categories = categoryIds.join(',');
          }
        }

        // Sort filter
        if (sort === 'price-low-high') {
          params.sort = 'price';
          params.order = 'asc';
        } else if (sort === 'price-high-low') {
          params.sort = 'price';
          params.order = 'desc';
        } else {
          // Default to newest
          params.sort = 'createdAt';
          params.order = 'desc';
        }


        // Size filter
        if (size !== 'all') {
          params.size = size;
        }

        const response = await getProducts(params);
        if (response.success) {
          setProducts(response.data);
        }
      } catch (err) {
        // console.error('Error fetching products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categorySlug, sort, size, categories]);

  // Update URL with filters
  const updateFilters = (filterType, value) => {
    const newParams = new URLSearchParams(location.search);

    if (filterType === 'sort') {
      setSortBy(value);
      newParams.set('sort', value);
    } else if (filterType === 'size') {
      setSizeFilter(value);
      newParams.set('size', value);
    }

    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  // Get category name for display
  const getCategoryName = () => {
    if (!currentCategory) return 'All Products';
    return currentCategory.name;
  };

  const seoTitle = currentCategory
    ? `${currentCategory.name} | Shop | TheMadZone`
    : 'Shop | TheMadZone';
  const seoDescription = currentCategory
    ? `Shop ${currentCategory.name} at TheMadZone. Premium streetwear fashion. Free shipping on orders over £100.`
    : 'Shop premium streetwear fashion at TheMadZone. Browse our latest collections, exclusive designs, and urban clothing. Free shipping on orders over £100.';

  return (
    <PageLayout>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={`${currentCategory?.name || ''}, streetwear, fashion, clothing, TheMadZone, shop`}
      />
      <ShopContainer>
        <HeaderContainer>
          <CategoryTitle>{getCategoryName()}</CategoryTitle>

          <FiltersContainer>
            <FilterSelect
              value={sort}
              onChange={(e) => updateFilters('sort', e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="price-low-high">Price: Low to High</option>
              <option value="price-high-low">Price: High to Low</option>
            </FilterSelect>

            <FilterSelect
              value={size}
              onChange={(e) => updateFilters('size', e.target.value)}
            >
              <option value="all">All Sizes</option>
              <option value="XS">XS</option>
              <option value="S">S</option>
              <option value="M">M</option>
              <option value="L">L</option>
              <option value="XL">XL</option>
              <option value="XXL">XXL</option>
            </FilterSelect>
          </FiltersContainer>
        </HeaderContainer>

        {/* Show child categories if we're viewing a parent category */}
        {parentCategory && childCategories.length > 0 && (
          <CategoryTagsContainer>
            {childCategories.map(category => (
              <CategoryTag
                key={category._id}
                to={`/shop/${category.slug}`}
                active={categorySlug === category.slug}
              >
                {category.name}
              </CategoryTag>
            ))}
          </CategoryTagsContainer>
        )}

        {loading ? (
          <LoadingMessage>Loading products...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : products.length === 0 ? (
          <NoProductsMessage>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-16 h-16">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <h3>Coming Soon</h3>
            <p>We're working hard to make this collection available for you.</p>
            <p>Thank you for your patience and continued support.</p>
          </NoProductsMessage>
        ) : (
          <ProductGrid>
            {products.map(product => (
              <ProductCard key={product._id} to={`/product/${product.slug}`}>
                <ProductImageContainer>
                  <ProductImage
                    src={product.mainImage?.url || '/images/placeholder.jpg'}
                    alt={product.title}
                  />
                </ProductImageContainer>
                <ProductInfo>
                  <ProductName>{product.title}</ProductName>
                  <ProductPrice>£{product.price}</ProductPrice>
                </ProductInfo>
              </ProductCard>
            ))}
          </ProductGrid>
        )}

        <PromoCarousel images={promoImages} />
      </ShopContainer>
    </PageLayout>
  );
};

export default ShopPage;
